import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { DashboardLayout } from '../components/layout';
import { useAdminNavigation } from '../hooks/useAdminNavigation';
import { Search } from 'lucide-react';
import * as adminApi from '../../../api/adminApi';
import styles from './SyncedDocsPage.module.css';

type Tab = 'documents' | 'transcripts' | 's3files';

const TAG_COLORS = [
  { bg: 'rgba(68,76,231,0.13)',  color: 'var(--ds-info-text)', border: 'rgba(68,76,231,0.3)' },
  { bg: 'rgba(97, 114, 243, 0.13)',  color: 'var(--ds-info-text)', border: 'rgba(97, 114, 243, 0.3)' },
  { bg: 'rgba(68, 76, 231, 0.13)',  color: 'var(--ds-link)', border: 'rgba(68, 76, 231, 0.3)' },
  { bg: 'rgba(240, 68, 56, 0.13)',   color: 'var(--ds-danger-text)', border: 'rgba(240, 68, 56, 0.3)'  },
  { bg: 'rgba(97,114,243,0.13)',  color: 'var(--ds-text)', border: 'rgba(97,114,243,0.3)' },
  { bg: 'rgba(164, 188, 253, 0.13)',  color: 'var(--ds-link)', border: 'rgba(164, 188, 253, 0.3)' },
  { bg: 'rgba(240, 68, 56, 0.13)',  color: 'var(--ds-danger-text)', border: 'rgba(240, 68, 56, 0.3)' },
  { bg: 'rgba(97, 114, 243, 0.13)',  color: 'var(--ds-info-text)', border: 'rgba(97, 114, 243, 0.3)' },
];

const getTagColor = (tag: string) => {
  let hash = 0;
  for (let i = 0; i < tag.length; i++) hash = tag.charCodeAt(i) + ((hash << 5) - hash);
  return TAG_COLORS[Math.abs(hash) % TAG_COLORS.length];
};

const parseTags = (raw: string): string[] =>
  raw.split(',').map(t => t.trim()).filter(Boolean);

const TagChip: React.FC<{ tag: string }> = ({ tag }) => {
  const { bg, color, border } = getTagColor(tag);
  return (
    <span style={{ fontSize: '0.72rem', background: bg, color, border: `1px solid ${border}`, borderRadius: 999, padding: '0.05rem 0.45rem', whiteSpace: 'nowrap', fontWeight: 600 }}>
      {tag}
    </span>
  );
};

const SyncedDocsPage: React.FC = () => {
  const {
    sidebarCollapsed,
    setSidebarCollapsed,
    activeSection,
    setSelectedRegulation,
    handleNavClick,
    handleLogout,
  } = useAdminNavigation();

  const [activeTab, setActiveTab] = useState<Tab>('documents');
  const [documents, setDocuments] = useState<adminApi.AdminDocument[]>([]);
  const [transcripts, setTranscripts] = useState<adminApi.AdminYoutubeTranscript[]>([]);
  const [docsLoading, setDocsLoading] = useState(true);
  const [transcriptsLoading, setTranscriptsLoading] = useState(true);
  const [docsError, setDocsError] = useState<string | null>(null);
  const [transcriptsError, setTranscriptsError] = useState<string | null>(null);

  // S3 Files tab state
  const [s3Files, setS3Files] = useState<adminApi.S3File[]>([]);
  const [s3FilesLoading, setS3FilesLoading] = useState(false);
  const [s3FilesError, setS3FilesError] = useState<string | null>(null);
  const [s3Search, setS3Search] = useState('');
  const [s3Page, setS3Page] = useState(1);

  // S3 Edit/Register modal state
  const [s3EditFile, setS3EditFile] = useState<adminApi.S3File | null>(null);
  const [s3EditName, setS3EditName] = useState('');
  const [s3EditType, setS3EditType] = useState('');
  const [s3EditJurisdiction, setS3EditJurisdiction] = useState('');
  const [s3EditTags, setS3EditTags] = useState('');
  const [s3EditSaving, setS3EditSaving] = useState(false);
  const [s3EditError, setS3EditError] = useState<string | null>(null);

  // All available jurisdictions (from API)
  const [apiJurisdictions, setApiJurisdictions] = useState<string[]>([]);
  // All available document types (from API)
  const [apiDocumentTypes, setApiDocumentTypes] = useState<string[]>([]);

  // Bulk selection state
  const [selectedDocIds, setSelectedDocIds] = useState<Set<number>>(new Set());
  const [selectedTranscriptIds, setSelectedTranscriptIds] = useState<Set<number>>(new Set());
  const [selectedS3Keys, setSelectedS3Keys] = useState<Set<string>>(new Set());

  // Search/filter state
  const [docSearch, setDocSearch] = useState('');
  const [filterType, setFilterType] = useState('');
  const [filterJurisdiction, setFilterJurisdiction] = useState('');
  const [filterUploadedBy, setFilterUploadedBy] = useState('');
  const [filterTag, setFilterTag] = useState('');
  const [transcriptSearch, setTranscriptSearch] = useState('');

  // Pagination
  const PAGE_SIZE = 10;
  const [docPage, setDocPage] = useState(1);
  const [transcriptPage, setTranscriptPage] = useState(1);

  // Preview modal state
  const [previewDoc, setPreviewDoc] = useState<adminApi.AdminDocument | null>(null);

  // Derive unique filter options from loaded documents
  const typeOptions = useMemo(
    () => Array.from(new Set(documents.map(d => d.document_type).filter((v): v is string => !!v))).sort(),
    [documents]
  );
  const jurisdictionOptions = useMemo(
    () => Array.from(new Set(documents.map(d => d.jurisdiction).filter((v): v is string => !!v))).sort(),
    [documents]
  );
  const uploadedByOptions = useMemo(
    () => Array.from(new Set(documents.map(d => d.admin_name).filter((v): v is string => !!v))).sort(),
    [documents]
  );
  const tagOptions = useMemo(() => {
    const all: string[] = [];
    documents.forEach(d => { if (d.tags) parseTags(d.tags).forEach(t => all.push(t)); });
    return Array.from(new Set(all)).sort();
  }, [documents]);

  const token = window.localStorage.getItem('policyora_token');

  const fetchDocuments = useCallback(() => {
    if (!token) return;
    setDocsLoading(true);
    setDocsError(null);
    adminApi
      .getAdminDocuments(token)
      .then(setDocuments)
      .catch((err) => setDocsError(err.message || 'Failed to load documents'))
      .finally(() => setDocsLoading(false));
  }, [token]);

  const fetchTranscripts = useCallback(() => {
    if (!token) return;
    setTranscriptsLoading(true);
    setTranscriptsError(null);
    adminApi
      .getAdminYoutubeTranscripts(token)
      .then(setTranscripts)
      .catch((err) => setTranscriptsError(err.message || 'Failed to load transcripts'))
      .finally(() => setTranscriptsLoading(false));
  }, [token]);

  const fetchS3Files = useCallback(() => {
    if (!token) return;
    setS3FilesLoading(true);
    setS3FilesError(null);
    adminApi
      .getS3Files(token)
      .then(setS3Files)
      .catch((err) => setS3FilesError(err.message || 'Failed to list S3 files'))
      .finally(() => setS3FilesLoading(false));
  }, [token]);

  useEffect(() => {
    fetchDocuments();
    fetchTranscripts();
    fetchS3Files();
    if (token) {
      adminApi.getJurisdictions(token)
        .then(list => setApiJurisdictions(list.map(j => j.name).sort()))
        .catch(() => {
          setApiJurisdictions(['US Federal', 'US State', 'European Union', 'United Kingdom', 'India', 'China', 'Brazil', 'Singapore']);
        });
      const BUILT_IN_TYPES = ['policy', 'legal_docs', 'reports', 'custom'];
      let customTypes: string[] = [];
      try { customTypes = JSON.parse(localStorage.getItem('admin_custom_doc_types') || '[]'); } catch { /* ignore */ }
      adminApi.getAdminDocumentTypes(token)
        .then(inUseTypes => {
          const merged = Array.from(new Set([...BUILT_IN_TYPES, ...customTypes, ...inUseTypes])).sort();
          setApiDocumentTypes(merged);
        })
        .catch(() => {
          const merged = Array.from(new Set([...BUILT_IN_TYPES, ...customTypes])).sort();
          setApiDocumentTypes(merged);
        });
    }
  }, [fetchDocuments, fetchTranscripts, fetchS3Files, token]);

  const handleBulkDeleteDocs = async () => {
    if (!token || selectedDocIds.size === 0) return;
    if (!window.confirm(`Delete ${selectedDocIds.size} document(s) permanently?`)) return;
    const ids = Array.from(selectedDocIds);
    try {
      await adminApi.bulkDeleteAdminDocuments(token, ids);
      setDocuments(prev => prev.filter(d => !selectedDocIds.has(d.id)));
      setSelectedDocIds(new Set());
    } catch (err) { window.alert((err as Error).message || 'Bulk delete failed'); }
  };

  const handleBulkDeleteTranscripts = async () => {
    if (!token || selectedTranscriptIds.size === 0) return;
    if (!window.confirm(`Delete ${selectedTranscriptIds.size} transcript(s) permanently?`)) return;
    const ids = Array.from(selectedTranscriptIds);
    try {
      await adminApi.bulkDeleteAdminTranscripts(token, ids);
      setTranscripts(prev => prev.filter(t => !selectedTranscriptIds.has(t.id)));
      setSelectedTranscriptIds(new Set());
    } catch (err) { window.alert((err as Error).message || 'Bulk delete failed'); }
  };

  const handleBulkDeleteS3 = async () => {
    if (!token || selectedS3Keys.size === 0) return;
    if (!window.confirm(`Delete ${selectedS3Keys.size} S3 file(s) permanently? This cannot be undone.`)) return;
    const keys = Array.from(selectedS3Keys);
    try {
      await adminApi.bulkDeleteS3Files(token, keys);
      setS3Files(prev => prev.filter(f => !selectedS3Keys.has(f.key)));
      setSelectedS3Keys(new Set());
    } catch (err) { window.alert((err as Error).message || 'Bulk delete failed'); }
  };

  // Lazy-load S3 files only when that tab is opened
  useEffect(() => {
    if (activeTab === 's3files' && s3Files.length === 0 && !s3FilesLoading) {
      fetchS3Files();
    }
  }, [activeTab]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleDeleteDocument = async (id: number) => {
    if (!token) return;
    if (!window.confirm('Delete this document permanently?')) return;
    try {
      await adminApi.deleteAdminDocument(token, id);
      setDocuments((prev) => prev.filter((d) => d.id !== id));
    } catch (err) {
      window.alert((err as Error).message || 'Failed to delete document');
    }
  };

  const handleDeleteTranscript = async (id: number) => {
    if (!token) return;
    if (!window.confirm('Delete this transcript?')) return;
    try {
      await adminApi.deleteAdminYoutubeTranscript(token, id);
      setTranscripts((prev) => prev.filter((t) => t.id !== id));
    } catch (err) {
      window.alert((err as Error).message || 'Failed to delete transcript');
    }
  };

  const filteredDocs = useMemo(() => documents.filter((d) => {
    const q = docSearch.toLowerCase();
    const matchesSearch =
      (d.stored_name || d.original_filename).toLowerCase().includes(q) ||
      (d.document_type || '').toLowerCase().includes(q) ||
      (d.jurisdiction || '').toLowerCase().includes(q);
    const matchesType = !filterType || d.document_type === filterType;
    const matchesJurisdiction = !filterJurisdiction || d.jurisdiction === filterJurisdiction;
    const matchesUploadedBy = !filterUploadedBy || d.admin_name === filterUploadedBy;
    const matchesTag = !filterTag || (!!d.tags && parseTags(d.tags).includes(filterTag));
    return matchesSearch && matchesType && matchesJurisdiction && matchesUploadedBy && matchesTag;
  }), [documents, docSearch, filterType, filterJurisdiction, filterUploadedBy, filterTag]);

  const filteredTranscripts = useMemo(() => transcripts.filter((t) => {
    const q = transcriptSearch.toLowerCase();
    return (
      (t.title || '').toLowerCase().includes(q) ||
      t.youtube_url.toLowerCase().includes(q)
    );
  }), [transcripts, transcriptSearch]);

  // Reset to page 1 when filters change
  useEffect(() => { setDocPage(1); }, [docSearch, filterType, filterJurisdiction, filterUploadedBy, filterTag]);
  useEffect(() => { setTranscriptPage(1); }, [transcriptSearch]);
  useEffect(() => { setS3Page(1); }, [s3Search]);

  const filteredS3Files = useMemo(() => {
    const q = s3Search.toLowerCase();
    return s3Files.filter(f =>
      f.filename.toLowerCase().includes(q) ||
      f.key.toLowerCase().includes(q) ||
      f.prefix.toLowerCase().includes(q)
    );
  }, [s3Files, s3Search]);

  const s3TotalPages = Math.max(1, Math.ceil(filteredS3Files.length / PAGE_SIZE));
  const pagedS3Files = filteredS3Files.slice((s3Page - 1) * PAGE_SIZE, s3Page * PAGE_SIZE);

  const docTotalPages = Math.max(1, Math.ceil(filteredDocs.length / PAGE_SIZE));
  const pagedDocs = filteredDocs.slice((docPage - 1) * PAGE_SIZE, docPage * PAGE_SIZE);

  const transcriptTotalPages = Math.max(1, Math.ceil(filteredTranscripts.length / PAGE_SIZE));
  const pagedTranscripts = filteredTranscripts.slice((transcriptPage - 1) * PAGE_SIZE, transcriptPage * PAGE_SIZE);

  const formatSize = (bytes?: number | null) => {
    if (!bytes) return '—';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  return (
    <DashboardLayout
      collapsed={sidebarCollapsed}
      activeSection={activeSection}
      welcomeLabel="Synced S3 content"
      selectedRegulation={null}
      onToggleSidebar={() => setSidebarCollapsed((prev) => !prev)}
      onNavClick={handleNavClick}
      onProfileClick={() => handleNavClick('profile')}
      onLogout={handleLogout}
      onRegulationModalClose={() => setSelectedRegulation(null)}
    >
      <section className={styles.page}>
        {/* Header */}
        <div className={styles.header}>
          <div className={styles.headerLeft}>
            <h2 className={styles.title}>Synced Content</h2>
            <p className={styles.subtitle}>
              Browse all documents and transcripts stored
            </p>
          </div>
          <button
            type="button"
            className={styles.backButton}
            onClick={() => handleNavClick('documents')}
          >
            ← Back to Documents
          </button>
        </div>

        {/* Tabs */}
        <div className={styles.tabBar}>
          <button
            type="button"
            className={`${styles.tabBtn} ${activeTab === 'documents' ? styles.tabBtnActive : ''}`}
            style={activeTab === 'documents' ? { background: 'rgba(68,76,231,0.1)', borderColor: 'var(--primary-500)', color: 'var(--ds-info-text)' } : {}}
            onClick={() => setActiveTab('documents')}
          >
            Documents
            <span className={styles.tabCount} style={{ background: 'rgba(68,76,231,0.12)', color: 'var(--ds-info-text)' }}>{documents.length.toLocaleString()}</span>
          </button>
          <button
            type="button"
            className={`${styles.tabBtn} ${activeTab === 'transcripts' ? styles.tabBtnActive : ''}`}
            style={activeTab === 'transcripts' ? { background: 'rgba(97, 114, 243, 0.1)', borderColor: 'var(--ds-success)', color: 'var(--ds-info-text)' } : {}}
            onClick={() => setActiveTab('transcripts')}
          >
            Transcripts
            <span className={styles.tabCount} style={{ background: 'rgba(97, 114, 243, 0.12)', color: 'var(--ds-info-text)' }}>{transcripts.length.toLocaleString()}</span>
          </button>
          <button
            type="button"
            className={`${styles.tabBtn} ${activeTab === 's3files' ? styles.tabBtnActive : ''}`}
            style={activeTab === 's3files' ? { background: 'rgba(68, 76, 231, 0.1)', borderColor: 'var(--ds-brand-blue)', color: 'var(--ds-link)' } : {}}
            onClick={() => setActiveTab('s3files')}
          >
            Direct Uploads
            <span className={styles.tabCount} style={{ background: 'rgba(68, 76, 231, 0.12)', color: 'var(--ds-link)' }}>{s3Files.length.toLocaleString()}</span>
          </button>
        </div>

        {/* ── Documents Tab ── */}
        {activeTab === 'documents' && (
          <div className={styles.tabPanel}>
            <div className={styles.toolbar}>
              <div className={styles.searchWrap}>
                <span className={styles.searchIcon}><Search size={16} aria-hidden /></span>
                <input
                  type="text"
                  placeholder="Search documents…"
                  className={styles.searchInput}
                  value={docSearch}
                  onChange={(e) => setDocSearch(e.target.value)}
                />
              </div>
              <div className={styles.selectWrap}>
                <select
                  className={styles.filterSelect}
                  value={filterType}
                  onChange={(e) => setFilterType(e.target.value)}
                >
                  <option value="">All types</option>
                  {typeOptions.map(t => (
                    <option key={t} value={t}>{t}</option>
                  ))}
                </select>
              </div>
              <div className={styles.selectWrap}>
                <select
                  className={styles.filterSelect}
                  value={filterJurisdiction}
                  onChange={(e) => setFilterJurisdiction(e.target.value)}
                >
                  <option value="">All jurisdictions</option>
                  {jurisdictionOptions.map(j => (
                    <option key={j} value={j}>{j}</option>
                  ))}
                </select>
              </div>
              <div className={styles.selectWrap}>
                <select
                  className={styles.filterSelect}
                  value={filterUploadedBy}
                  onChange={(e) => setFilterUploadedBy(e.target.value)}
                >
                  <option value="">All uploaders</option>
                  {uploadedByOptions.map(u => (
                    <option key={u} value={u}>{u}</option>
                  ))}
                </select>
              </div>
              {tagOptions.length > 0 && (
                <div className={styles.selectWrap}>
                  <select
                    className={styles.filterSelect}
                    value={filterTag}
                    onChange={(e) => setFilterTag(e.target.value)}
                  >
                    <option value="">All tags</option>
                    {tagOptions.map(tag => (
                      <option key={tag} value={tag}>{tag}</option>
                    ))}
                  </select>
                </div>
              )}
              {(filterType || filterJurisdiction || filterUploadedBy || filterTag) && (
                <button
                  type="button"
                  className={styles.clearFiltersBtn}
                  onClick={() => { setFilterType(''); setFilterJurisdiction(''); setFilterUploadedBy(''); setFilterTag(''); }}
                >
                  ✕ Clear
                </button>
              )}
              <button
                type="button"
                className={styles.refreshBtn}
                onClick={fetchDocuments}
                title="Refresh"
              >
                ↺ Refresh
              </button>
              {selectedDocIds.size > 0 && (
                <button
                  type="button"
                  className={styles.deleteBtn}
                  onClick={handleBulkDeleteDocs}
                >
                  Delete selected ({selectedDocIds.size})
                </button>
              )}
            </div>

            {docsLoading ? (
              <div className={styles.stateBox}>
                <div className={styles.spinner} />
                <span>Loading documents…</span>
              </div>
            ) : docsError ? (
              <div className={styles.errorBox}>
                <span>{docsError}</span>
                <button type="button" className={styles.retryBtn} onClick={fetchDocuments}>
                  Retry
                </button>
              </div>
            ) : filteredDocs.length === 0 ? (
              <div className={styles.stateBox}>No documents found</div>
            ) : (
              <div className={styles.tableWrap}>
                <table className={styles.table}>
                  <thead>
                    <tr>
                      <th style={{ width: 36 }}>
                        <input
                          type="checkbox"
                          title="Select all"
                          checked={pagedDocs.length > 0 && pagedDocs.every(d => selectedDocIds.has(d.id))}
                          onChange={e => {
                            const next = new Set(selectedDocIds);
                            if (e.target.checked) pagedDocs.forEach(d => next.add(d.id));
                            else pagedDocs.forEach(d => next.delete(d.id));
                            setSelectedDocIds(next);
                          }}
                        />
                      </th>
                      <th>Name</th>
                      <th>Type</th>
                      <th>Jurisdiction</th>
                      <th>Tags</th>
                      <th>Size</th>
                      <th>Uploaded</th>
                      <th>Uploaded by</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {pagedDocs.map((doc) => (
                      <tr key={doc.id} style={{ background: selectedDocIds.has(doc.id) ? 'rgba(68,76,231,0.06)' : undefined }}>
                        <td>
                          <input
                            type="checkbox"
                            checked={selectedDocIds.has(doc.id)}
                            onChange={e => {
                              const next = new Set(selectedDocIds);
                              if (e.target.checked) next.add(doc.id); else next.delete(doc.id);
                              setSelectedDocIds(next);
                            }}
                          />
                        </td>
                        <td className={styles.nameCell}>
                          {doc.stored_name || doc.original_filename}
                        </td>
                        <td>
                          <span className={styles.badge}>{doc.document_type || '—'}</span>
                        </td>
                        <td>{doc.jurisdiction || '—'}</td>
                        <td>
                          {doc.tags && parseTags(doc.tags).length > 0 ? (
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.25rem' }}>
                              {parseTags(doc.tags).map(tag => <TagChip key={tag} tag={tag} />)}
                            </div>
                          ) : (
                            <span style={{ color: 'var(--ds-text-faint)', fontSize: '0.82rem' }}>—</span>
                          )}
                        </td>
                        <td>{formatSize(doc.file_size)}</td>
                        <td>
                          {doc.created_at
                            ? new Date(doc.created_at).toLocaleDateString()
                            : '—'}
                        </td>
                        <td>{doc.admin_name || '—'}</td>
                        <td>
                          <div className={styles.actions}>
                            <button
                              type="button"
                              className={styles.ghostBtn}
                              onClick={() => setPreviewDoc(doc)}
                            >
                              Preview
                            </button>
                            {doc.download_url && (
                              <a
                                href={doc.download_url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className={styles.linkBtn}
                              >
                                Download
                              </a>
                            )}
                            <button
                              type="button"
                              className={styles.deleteBtn}
                              onClick={() => handleDeleteDocument(doc.id)}
                            >
                              Delete
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            <div className={styles.paginationRow}>
              <button
                type="button"
                className={styles.pageBtn}
                disabled={docPage <= 1}
                onClick={() => setDocPage(p => p - 1)}
              >← Prev</button>
              <span className={styles.pageInfo}>Page {docPage} of {docTotalPages} &nbsp;·&nbsp; {filteredDocs.length} of {documents.length} docs</span>
              <button
                type="button"
                className={styles.pageBtn}
                disabled={docPage >= docTotalPages}
                onClick={() => setDocPage(p => p + 1)}
              >Next →</button>
            </div>
          </div>
        )}

        {/* ── Transcripts Tab ── */}
        {activeTab === 'transcripts' && (
          <div className={styles.tabPanel}>
            <div className={styles.toolbar}>
              <div className={styles.searchWrap}>
                <span className={styles.searchIcon}><Search size={16} aria-hidden /></span>
                <input
                  type="text"
                  placeholder="Search transcripts…"
                  className={styles.searchInput}
                  value={transcriptSearch}
                  onChange={(e) => setTranscriptSearch(e.target.value)}
                />
              </div>
              <button
                type="button"
                className={styles.refreshBtn}
                onClick={fetchTranscripts}
                title="Refresh"
              >
                ↺ Refresh
              </button>
              {selectedTranscriptIds.size > 0 && (
                <button
                  type="button"
                  className={styles.deleteBtn}
                  onClick={handleBulkDeleteTranscripts}
                >
                  Delete selected ({selectedTranscriptIds.size})
                </button>
              )}
            </div>

            {transcriptsLoading ? (
              <div className={styles.stateBox}>
                <div className={styles.spinner} />
                <span>Loading transcripts…</span>
              </div>
            ) : transcriptsError ? (
              <div className={styles.errorBox}>
                <span>{transcriptsError}</span>
                <button type="button" className={styles.retryBtn} onClick={fetchTranscripts}>
                  Retry
                </button>
              </div>
            ) : filteredTranscripts.length === 0 ? (
              <div className={styles.stateBox}>No transcripts found</div>
            ) : (
              <div className={styles.tableWrap}>
                <table className={styles.table}>
                  <thead>
                    <tr>
                      <th style={{ width: 36 }}>
                        <input
                          type="checkbox"
                          title="Select all"
                          checked={pagedTranscripts.length > 0 && pagedTranscripts.every(t => selectedTranscriptIds.has(t.id))}
                          onChange={e => {
                            const next = new Set(selectedTranscriptIds);
                            if (e.target.checked) pagedTranscripts.forEach(t => next.add(t.id));
                            else pagedTranscripts.forEach(t => next.delete(t.id));
                            setSelectedTranscriptIds(next);
                          }}
                        />
                      </th>
                      <th>Title</th>
                      <th>YouTube URL</th>
                      <th>Language</th>
                      <th>Created</th>
                      <th>Uploaded by</th>
                      <th>Status</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {pagedTranscripts.map((t) => (
                      <tr key={t.id} style={{ background: selectedTranscriptIds.has(t.id) ? 'rgba(97, 114, 243, 0.06)' : undefined }}>
                        <td>
                          <input
                            type="checkbox"
                            checked={selectedTranscriptIds.has(t.id)}
                            onChange={e => {
                              const next = new Set(selectedTranscriptIds);
                              if (e.target.checked) next.add(t.id); else next.delete(t.id);
                              setSelectedTranscriptIds(next);
                            }}
                          />
                        </td>
                        <td className={styles.nameCell}>
                          {t.title || 'Untitled transcript'}
                        </td>
                        <td className={styles.urlCell}>
                          <a
                            href={t.youtube_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className={styles.urlLink}
                            title={t.youtube_url}
                          >
                            {t.youtube_url.length > 45
                              ? t.youtube_url.slice(0, 45) + '…'
                              : t.youtube_url}
                          </a>
                        </td>
                        <td>{t.language || '—'}</td>
                        <td>
                          {t.created_at
                            ? new Date(t.created_at).toLocaleDateString()
                            : '—'}
                        </td>
                        <td>{t.admin_name || '—'}</td>
                        <td>
                          <span
                            className={`${styles.badge} ${t.download_url ? styles.badgeDone : styles.badgePending}`}
                          >
                            {t.download_url ? 'Completed' : 'Processing'}
                          </span>
                        </td>
                        <td>
                          <div className={styles.actions}>
                            {t.download_url && (
                              <a
                                href={t.download_url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className={styles.ghostBtn}
                              >
                                Preview
                              </a>
                            )}
                            {t.download_url && (
                              <a
                                href={t.download_url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className={styles.linkBtn}
                              >
                                Download
                              </a>
                            )}
                            <button
                              type="button"
                              className={styles.deleteBtn}
                              onClick={() => handleDeleteTranscript(t.id)}
                            >
                              Delete
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            <div className={styles.paginationRow}>
              <button
                type="button"
                className={styles.pageBtn}
                disabled={transcriptPage <= 1}
                onClick={() => setTranscriptPage(p => p - 1)}
              >← Prev</button>
              <span className={styles.pageInfo}>Page {transcriptPage} of {transcriptTotalPages} &nbsp;·&nbsp; {filteredTranscripts.length} of {transcripts.length} transcripts</span>
              <button
                type="button"
                className={styles.pageBtn}
                disabled={transcriptPage >= transcriptTotalPages}
                onClick={() => setTranscriptPage(p => p + 1)}
              >Next →</button>
            </div>
          </div>
        )}

        {/* ── S3 Files Tab ── */}
        {activeTab === 's3files' && (
          <div className={styles.tabPanel}>
            <div className={styles.toolbar}>
              <div className={styles.searchWrap}>
                <span className={styles.searchIcon}><Search size={16} aria-hidden /></span>
                <input
                  type="text"
                  placeholder="Search files…"
                  className={styles.searchInput}
                  value={s3Search}
                  onChange={(e) => setS3Search(e.target.value)}
                />
              </div>
              <button
                type="button"
                className={styles.refreshBtn}
                onClick={fetchS3Files}
                title="Refresh"
              >
                ↺ Refresh
              </button>
              {selectedS3Keys.size > 0 && (
                <button
                  type="button"
                  className={styles.deleteBtn}
                  onClick={handleBulkDeleteS3}
                >
                  Delete selected ({selectedS3Keys.size})
                </button>
              )}
            </div>

            {s3FilesLoading ? (
              <div className={styles.stateBox}>
                <div className={styles.spinner} />
                <span>Loading S3 files…</span>
              </div>
            ) : s3FilesError ? (
              <div className={styles.errorBox}>
                <span>{s3FilesError}</span>
                <button type="button" className={styles.retryBtn} onClick={fetchS3Files}>Retry</button>
              </div>
            ) : filteredS3Files.length === 0 ? (
              <div className={styles.stateBox}>No files found in S3</div>
            ) : (
              <div className={styles.tableWrap}>
                <table className={styles.table}>
                  <thead>
                    <tr>
                      <th style={{ width: 36 }}>
                        <input
                          type="checkbox"
                          title="Select all"
                          checked={pagedS3Files.length > 0 && pagedS3Files.every(f => selectedS3Keys.has(f.key))}
                          onChange={e => {
                            const next = new Set(selectedS3Keys);
                            if (e.target.checked) pagedS3Files.forEach(f => next.add(f.key));
                            else pagedS3Files.forEach(f => next.delete(f.key));
                            setSelectedS3Keys(next);
                          }}
                        />
                      </th>
                      <th>File Name</th>
                      <th>Size</th>
                      <th>Last Modified</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {pagedS3Files.map((f) => (
                      <tr key={f.key} style={{ background: selectedS3Keys.has(f.key) ? 'rgba(68, 76, 231, 0.06)' : undefined }}>
                        <td>
                          <input
                            type="checkbox"
                            checked={selectedS3Keys.has(f.key)}
                            onChange={e => {
                              const next = new Set(selectedS3Keys);
                              if (e.target.checked) next.add(f.key); else next.delete(f.key);
                              setSelectedS3Keys(next);
                            }}
                          />
                        </td>
                        <td className={styles.nameCell}>
                          {f.filename}
                        </td>
                        <td>{formatSize(f.size)}</td>
                        <td>{f.last_modified ? new Date(f.last_modified).toLocaleDateString() : '—'}</td>
                        <td>
                          <div className={styles.actions}>
                            <button
                              type="button"
                              className={styles.ghostBtn}
                              onClick={() => {
                                setS3EditFile(f);
                                setS3EditName(f.filename);
                                setS3EditType('');
                                setS3EditJurisdiction('');
                                setS3EditTags('');
                                setS3EditError(null);
                              }}
                            >
                              Edit
                            </button>
                            {f.download_url && (
                              <a href={f.download_url} target="_blank" rel="noopener noreferrer" className={styles.ghostBtn}>
                                Open
                              </a>
                            )}
                            {f.download_url && (
                              <a href={f.download_url} target="_blank" rel="noopener noreferrer" className={styles.linkBtn}>
                                Download
                              </a>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            <div className={styles.paginationRow}>
              <button type="button" className={styles.pageBtn} disabled={s3Page <= 1} onClick={() => setS3Page(p => p - 1)}>← Prev</button>
              <span className={styles.pageInfo}>Page {s3Page} of {s3TotalPages} &nbsp;·&nbsp; {filteredS3Files.length} of {s3Files.length} files</span>
              <button type="button" className={styles.pageBtn} disabled={s3Page >= s3TotalPages} onClick={() => setS3Page(p => p + 1)}>Next →</button>
            </div>
          </div>
        )}
      </section>

      {/* ── S3 Edit / Register Modal ── */}
      {s3EditFile && (
        <div
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', zIndex: 1100, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem' }}
          onClick={() => setS3EditFile(null)}
        >
          <div
            style={{ background: 'var(--admin-surface,#fff)', borderRadius: 12, width: '100%', maxWidth: 520, boxShadow: '0 24px 56px rgba(0,0,0,0.3)', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}
            onClick={e => e.stopPropagation()}
          >
            {/* header */}
            <div style={{ padding: '1rem 1.25rem', borderBottom: '1px solid var(--admin-border,var(--ds-border))', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <span style={{ fontWeight: 700, fontSize: '1rem', color: 'var(--admin-text,var(--ds-text))' }}>Register S3 File</span>
              <button type="button" onClick={() => setS3EditFile(null)} style={{ background: 'none', border: 'none', fontSize: '1.2rem', cursor: 'pointer', color: 'var(--admin-text-muted,var(--ds-text-muted))' }}>✕</button>
            </div>
            {/* form */}
            <div style={{ padding: '1.25rem', display: 'flex', flexDirection: 'column', gap: '0.9rem' }}>
              <div style={{ fontSize: '0.78rem', color: 'var(--admin-text-muted,var(--ds-text-muted))', wordBreak: 'break-all' }}>Key: {s3EditFile.key}</div>
              <label style={{ display: 'flex', flexDirection: 'column', gap: '0.3rem', fontWeight: 600, fontSize: '0.85rem', color: 'var(--admin-text,var(--ds-text))' }}>
                Name
                <input
                  type="text"
                  value={s3EditName}
                  onChange={e => setS3EditName(e.target.value)}
                  placeholder={s3EditFile.filename}
                  style={{ padding: '0.45rem 0.7rem', borderRadius: 7, border: '1px solid var(--admin-border,var(--ds-border-strong))', fontSize: '0.875rem', outline: 'none' }}
                />
              </label>
              <label style={{ display: 'flex', flexDirection: 'column', gap: '0.3rem', fontWeight: 600, fontSize: '0.85rem', color: 'var(--admin-text,var(--ds-text))' }}>
                Document Type
                <select
                  value={s3EditType}
                  onChange={e => setS3EditType(e.target.value)}
                  style={{ padding: '0.45rem 0.7rem', borderRadius: 7, border: '1px solid var(--admin-border,var(--ds-border-strong))', fontSize: '0.875rem', outline: 'none', background: 'var(--admin-surface,#fff)', color: 'var(--admin-text,var(--ds-text))' }}
                >
                  <option value="">— Select type —</option>
                  {apiDocumentTypes.map(t => (
                    <option key={t} value={t}>{t}</option>
                  ))}
                </select>
              </label>
              <label style={{ display: 'flex', flexDirection: 'column', gap: '0.3rem', fontWeight: 600, fontSize: '0.85rem', color: 'var(--admin-text,var(--ds-text))' }}>
                Jurisdiction
                <select
                  value={s3EditJurisdiction}
                  onChange={e => setS3EditJurisdiction(e.target.value)}
                  style={{ padding: '0.45rem 0.7rem', borderRadius: 7, border: '1px solid var(--admin-border,var(--ds-border-strong))', fontSize: '0.875rem', outline: 'none', background: 'var(--admin-surface,#fff)', color: 'var(--admin-text,var(--ds-text))' }}
                >
                  <option value="">— Select jurisdiction —</option>
                  {Array.from(new Set([...apiJurisdictions, ...jurisdictionOptions])).sort().map(j => (
                    <option key={j} value={j}>{j}</option>
                  ))}
                </select>
              </label>
              <label style={{ display: 'flex', flexDirection: 'column', gap: '0.3rem', fontWeight: 600, fontSize: '0.85rem', color: 'var(--admin-text,var(--ds-text))' }}>
                Tags <span style={{ fontWeight: 400, color: 'var(--admin-text-muted,var(--ds-text-muted))' }}>(comma-separated)</span>
                <input
                  type="text"
                  value={s3EditTags}
                  onChange={e => setS3EditTags(e.target.value)}
                  placeholder="e.g. gdpr, privacy, 2024"
                  style={{ padding: '0.45rem 0.7rem', borderRadius: 7, border: '1px solid var(--admin-border,var(--ds-border-strong))', fontSize: '0.875rem', outline: 'none' }}
                />
              </label>
              {s3EditError && (
                <div style={{ fontSize: '0.82rem', color: 'var(--ds-danger-text)', background: 'rgba(240, 68, 56, 0.08)', borderRadius: 7, padding: '0.45rem 0.7rem' }}>{s3EditError}</div>
              )}
            </div>
            {/* footer */}
            <div style={{ padding: '0.9rem 1.25rem', borderTop: '1px solid var(--admin-border,var(--ds-border))', display: 'flex', gap: '0.6rem', justifyContent: 'flex-end' }}>
              <button
                type="button"
                onClick={() => setS3EditFile(null)}
                style={{ padding: '0.45rem 1rem', borderRadius: 7, border: '1px solid var(--admin-border,var(--ds-border-strong))', background: 'transparent', cursor: 'pointer', fontSize: '0.875rem', fontWeight: 600, color: 'var(--admin-text,var(--ds-text-secondary))' }}
              >
                Cancel
              </button>
              <button
                type="button"
                disabled={s3EditSaving}
                onClick={async () => {
                  const token = window.localStorage.getItem('policyora_token');
                  if (!token || !s3EditFile) return;
                  setS3EditSaving(true);
                  setS3EditError(null);
                  try {
                    await adminApi.registerS3File(token, {
                      key: s3EditFile.key,
                      name: s3EditName.trim() || s3EditFile.filename,
                      document_type: s3EditType.trim() || undefined,
                      jurisdiction: s3EditJurisdiction.trim() || undefined,
                      tags: s3EditTags.trim() || undefined,
                    });
                    // Remove from s3Files list (it's now a registered document)
                    setS3Files(prev => prev.filter(f => f.key !== s3EditFile.key));
                    setS3EditFile(null);
                  } catch (err: any) {
                    setS3EditError(err.message || 'Failed to register file');
                  } finally {
                    setS3EditSaving(false);
                  }
                }}
                style={{ padding: '0.45rem 1.1rem', borderRadius: 7, border: 'none', background: 'var(--ds-brand-blue)', color: 'var(--ds-text-on-dark)', cursor: s3EditSaving ? 'not-allowed' : 'pointer', fontSize: '0.875rem', fontWeight: 600, opacity: s3EditSaving ? 0.7 : 1 }}
              >
                {s3EditSaving ? 'Saving…' : 'Save & Register'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Document Preview Modal ── */}
      {previewDoc && (
        <div
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem' }}
          onClick={() => setPreviewDoc(null)}
        >
          <div
            style={{ background: 'var(--admin-surface,#fff)', borderRadius: 12, width: '90vw', maxWidth: 960, height: '85vh', display: 'flex', flexDirection: 'column', overflow: 'hidden', boxShadow: '0 25px 60px rgba(0,0,0,0.35)' }}
            onClick={e => e.stopPropagation()}
          >
            {/* Modal header */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '1rem 1.25rem', borderBottom: '1px solid var(--admin-border,var(--ds-border))' }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.35rem' }}>
                <span style={{ fontWeight: 700, fontSize: '1rem', color: 'var(--admin-text,var(--ds-text))' }}>
                  {previewDoc.stored_name || previewDoc.original_filename}
                </span>
                <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', alignItems: 'center' }}>
                  {previewDoc.document_type && (
                    <span style={{ fontSize: '0.75rem', background: 'rgba(68,76,231,0.1)', color: 'var(--ds-info-text)', border: '1px solid rgba(68,76,231,0.2)', borderRadius: 999, padding: '0.1rem 0.5rem', fontWeight: 600 }}>
                      {previewDoc.document_type}
                    </span>
                  )}
                  {previewDoc.jurisdiction && (
                    <span style={{ fontSize: '0.75rem', color: 'var(--admin-text-muted,var(--ds-text-muted))' }}>{previewDoc.jurisdiction}</span>
                  )}
                  {previewDoc.tags && parseTags(previewDoc.tags).map(tag => <TagChip key={tag} tag={tag} />)}
                </div>
              </div>
              <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                {previewDoc.download_url && (
                  <a
                    href={previewDoc.download_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ fontSize: '0.85rem', padding: '0.35rem 0.8rem', background: 'var(--primary-500)', color: 'var(--ds-text-on-dark)', borderRadius: 8, textDecoration: 'none', fontWeight: 600 }}
                  >
                    ↓ Download
                  </a>
                )}
                <button
                  type="button"
                  onClick={() => setPreviewDoc(null)}
                  style={{ background: 'transparent', border: 'none', fontSize: '1.3rem', cursor: 'pointer', color: 'var(--admin-text-muted,var(--ds-text-muted))', lineHeight: 1, padding: '0.2rem' }}
                >
                  ✕
                </button>
              </div>
            </div>
            {/* iframe preview */}
            <div style={{ flex: 1, overflow: 'hidden' }}>
              {previewDoc.download_url ? (
                <iframe
                  src={previewDoc.download_url}
                  title={previewDoc.stored_name || previewDoc.original_filename}
                  style={{ width: '100%', height: '100%', border: 'none' }}
                />
              ) : (
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', color: 'var(--admin-text-muted,var(--ds-text-faint))' }}>
                  No preview available
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </DashboardLayout>
  );
};

export default SyncedDocsPage;
